import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Plus, Copy, Check, Edit, Trash2, RefreshCw, User,
  Phone, Mail, Coffee, Users, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

export default function WaiterManagement() {
  const { t } = useTranslation();
  const { api } = useAuth();
  // Use shared restaurant context
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurant();
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const fetchWaiters = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/waiters`);
      setWaiters(response.data);
    } catch (error) {
      console.error('Failed to fetch waiters:', error);
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      fetchWaiters();
    }
  }, [selectedRestaurant?.id, fetchWaiters]);

  const handleCreateWaiter = async () => {
    if (!form.name || !form.email) {
      toast.error('Please fill in name and email');
      return;
    }

    try {
      const response = await api.post(`/restaurants/${selectedRestaurant.id}/waiters`, form);
      toast.success(`Waiter created! Login code: ${response.data.login_code}`);
      setShowDialog(false);
      setForm({ name: '', email: '', phone: '' });
      fetchWaiters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create waiter');
    }
  };

  const handleToggleActive = async (waiter) => {
    try {
      await api.put(`/restaurants/${selectedRestaurant.id}/waiters/${waiter.id}`, {
        is_active: !waiter.is_active
      });
      toast.success(waiter.is_active ? 'Waiter deactivated' : 'Waiter activated');
      fetchWaiters();
    } catch (error) {
      toast.error('Failed to update waiter');
    }
  };

  const handleRegenerateCode = async (waiter) => {
    try {
      const response = await api.post(`/restaurants/${selectedRestaurant.id}/waiters/${waiter.id}/regenerate-code`);
      toast.success(`New code: ${response.data.login_code}`);
      fetchWaiters();
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const handleDeleteWaiter = async (waiter) => {
    if (!window.confirm(`Delete waiter ${waiter.name}?`)) return;

    try {
      await api.delete(`/restaurants/${selectedRestaurant.id}/waiters/${waiter.id}`);
      toast.success('Waiter deleted');
      fetchWaiters();
    } catch (error) {
      toast.error('Failed to delete waiter');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(t('waiterMgmt.codeCopied'));
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading && waiters.length === 0) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waiter-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('waiterMgmt.title')}</h1>
          <p className="text-slate-500 mt-1">{t('waiterMgmt.subtitle') || "Manage your restaurant's waitstaff"}</p>
        </div>
        
        <Button onClick={() => setShowDialog(true)} className="btn-primary" data-testid="add-waiter-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('waiterMgmt.addWaiter')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-teal-600">{waiters.length}</p>
            <p className="text-sm text-slate-500">{t('waiterMgmt.totalWaiters') || 'Total Waiters'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-green-600">{waiters.filter(w => w.is_online).length}</p>
            <p className="text-sm text-slate-500">Online Now</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{waiters.filter(w => w.is_active).length}</p>
            <p className="text-sm text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-slate-400">{waiters.filter(w => !w.is_active).length}</p>
            <p className="text-sm text-slate-500">Inactive</p>
          </CardContent>
        </Card>
      </div>

      {/* Waiters List */}
      {waiters.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Coffee className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No waiters yet</h3>
            <p className="text-slate-500 mb-4">Add your first waiter to enable in-house ordering</p>
            <Button onClick={() => setShowDialog(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Add Waiter
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {waiters.map(waiter => (
            <Card key={waiter.id} className={!waiter.is_active ? 'opacity-60' : ''} data-testid={`waiter-${waiter.id}`}>
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      waiter.is_online ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <User className={`w-6 h-6 ${waiter.is_online ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{waiter.name}</h3>
                        {waiter.is_online && (
                          <Badge className="bg-green-100 text-green-700">Online</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" /> {waiter.email}
                        </span>
                        {waiter.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {waiter.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Login Code */}
                    <div className="bg-slate-100 px-4 py-2 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Login Code</p>
                      <div className="flex items-center gap-2">
                        <code className="text-lg font-mono font-bold text-teal-600">
                          {waiter.waiter_login_code}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 px-2"
                          onClick={() => copyCode(waiter.waiter_login_code)}
                        >
                          {copiedCode === waiter.waiter_login_code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateCode(waiter)}
                        title="Generate new code"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-500">Active</span>
                        <Switch
                          checked={waiter.is_active}
                          onCheckedChange={() => handleToggleActive(waiter)}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDeleteWaiter(waiter)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Waiter Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Waiter</DialogTitle>
            <DialogDescription>
              Create a waiter account. They'll receive a 4-digit login code.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                placeholder="Enter waiter's name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="waiter-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="waiter-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                type="tel"
                placeholder="Enter phone number"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                data-testid="waiter-phone-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateWaiter} className="btn-primary" data-testid="create-waiter-btn">
              Create Waiter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
